import { usePetsittingMode } from '@/store/usePetsittingMode'
import { useTranslation } from 'next-i18next'
import type { GetServerSideProps, GetServerSidePropsContext } from 'next'
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import NextImage from 'next/image'
import VariantImages from '@/assets/VariantImages'
import Box from '@mui/material/Box'
import { useRouter } from 'next/router'

import { SEO, withNoAuthRoute, withNoAuthRouteProps } from '@/components'
import { useFirstRouterQueryParam, useIsResponsive } from '@/hooks'
import React from 'react'
import { EmtCircularProgress } from 'emt-components-v2'
import SimpleLayout from '@/components/EMT/SimpleLayout'

import SignUpDetailsFormContent from '@/components/SignUp/SignUpDetailsFormContent'
import Routes from '@/assets/configs/Routes'

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
  }
}

type Props = withNoAuthRouteProps &
  EMTServerSideProps & {
    isBelgium: boolean
    isFrance: boolean
  }

const SignUpDetailsPage: React.FC<Props> = ({ url, isBelgium, isFrance }) => {
  const { t } = useTranslation('connection')
  const router = useRouter()
  const isPetsittingMode = usePetsittingMode.getState().isPetsittingMode

  const [isMobile] = useIsResponsive()

  const [type] = useFirstRouterQueryParam(['type'])
  const isQueryOwner = type === 'dogowner'

  const rightContent = (
    <EmtCircularProgress
      stepTitle={t('stepTitle') as string}
      color="white"
      value={isQueryOwner ? 20 : 17}
      currentStep={1}
      totalStep={type === 'pro' ? 7 : 3}
      currentStepTitle={t('sign-up.details.stepTitle') as string}
    />
  )

  return (
    <>
      <SEO url={`${url}`} subtitle={t(`sign-up.details.title.${type}`) as string} />

      <SimpleLayout
        title={t(`sign-up.details.title.${type}`)}
        rightContent={isPetsittingMode && isQueryOwner ? undefined : rightContent}
        goBack={isPetsittingMode && isMobile ? () => router.push(Routes.Connection.SignUp.Type) : undefined}
      >
        <SignUpDetailsFormContent
          type={type as string}
          fakeAnimal={isPetsittingMode}
          isBelgium={isBelgium}
          isFrance={isFrance}
          detailsPage={true}
        />

        <Box sx={{ maxWidth: '310px', marginLeft: 'auto', marginRight: 'auto' }}>
          <NextImage
            src={type === 'pro' ? VariantImages.FooterTopLoginSitter : VariantImages.FooterTopLogin}
            quality={100}
            alt=""
            style={{ objectFit: 'contain', maxWidth: '100%' }}
          />
        </Box>
      </SimpleLayout>
    </>
  )
}

const baseGetServerSideProps = getEMTServerSideProps({
  namespaces: ['common', 'connection'],
  i18nTitleNs: 'connection',
  i18nTitleKey: 'sign-up.title',
})

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const result = await baseGetServerSideProps(context)

  const country = context.req.headers['x-user-country'] ?? null
  const isBelgium = country === 'BE'
  const isFrance = country === 'FR'

  // Si la fonction de base a retourné un redirect ou notFound, on ne touche pas au résultat
  if ('redirect' in result || 'notFound' in result) {
    return result
  }

  return {
    ...result,
    props: {
      ...result.props,
      isBelgium,
      isFrance,
    },
  }
}

export default withNoAuthRoute()(SignUpDetailsPage)
